/**
 * Reset-password hook
 *
 * Provides a 3-step forgot-password flow: email -> OTP -> new password
 */

import { useAuthActions } from "@convex-dev/auth/react";
import { useMutation } from "convex/react";
import { useCallback, useState } from "react";
import { api } from "convex/_generated/api";
import i18n from "i18next";
import { normalizeEmail, validateEmail, sanitizeAuthError } from "../utils";

type ResetStep = "email" | "otp" | "new-password";

export interface UseResetPasswordOptions {
  /** Called on successful password reset */
  onSuccess?: () => void;
  /** Pre-set email for deep-link reset flow (skips requestReset) */
  initialEmail?: string;
}

export interface UseResetPasswordReturn {
  /** Current step in the reset flow */
  step: ResetStep;
  /** Whether an operation is in progress */
  isLoading: boolean;
  /** Current error message if any */
  error: string | null;
  /** Email used for OTP verification (shown in UI) */
  verificationEmail: string | null;
  /** Stored OTP code after step 2 (used in step 3) */
  pendingCode: string | null;
  /** Step 1: Request a reset OTP for the given email */
  requestReset: (email: string) => Promise<void>;
  /** Step 2: Store the OTP code and advance to new-password step (no backend call) */
  confirmCode: (code: string) => void;
  /** Step 3: Set the new password (validates + calls backend) */
  setNewPassword: (password: string, confirm: string) => Promise<void>;
  /** Resend the OTP code */
  resendCode: () => Promise<void>;
  /** Go back to email step (resets all state) */
  backToEmail: () => void;
  /** Go back to OTP step */
  backToOtp: () => void;
  /** Clear the error */
  clearError: () => void;
}

/**
 * Hook for the forgot-password / reset-password flow.
 *
 * 3-step state machine:
 * 1. email     - user enters their email, backend sends OTP
 * 2. otp       - user enters the OTP code (stored locally, no backend call)
 * 3. new-password - user enters new password, backend verifies OTP + resets password
 */
export function useResetPassword(
  options: UseResetPasswordOptions = {},
): UseResetPasswordReturn {
  const { signIn: convexSignIn } = useAuthActions();
  const setPendingLocale = useMutation(api.pending_locale_mutations.set);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<ResetStep>(
    options.initialEmail ? "otp" : "email",
  );
  const [verificationEmail, setVerificationEmail] = useState<string | null>(
    options.initialEmail ?? null,
  );
  const [pendingCode, setPendingCode] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const backToEmail = useCallback(() => {
    setStep("email");
    setError(null);
    setVerificationEmail(null);
    setPendingCode(null);
  }, []);

  const backToOtp = useCallback(() => {
    setStep("otp");
    setError(null);
  }, []);

  const requestReset = useCallback(
    async (email: string): Promise<void> => {
      setIsLoading(true);
      setError(null);

      const normalizedEmail = normalizeEmail(email);

      // Validate email format
      if (!validateEmail(normalizedEmail)) {
        setError(i18n.t("auth:errors.invalidEmail"));
        setIsLoading(false);
        return;
      }

      try {
        // Store user's chosen language so the OTP email is sent in the right language
        await setPendingLocale({
          email: normalizedEmail,
          locale: i18n.language ?? "de",
        });

        const formData = new FormData();
        formData.set("email", normalizedEmail);
        formData.set("flow", "reset");

        await convexSignIn("password", formData);

        // Success: store email and advance to OTP step
        setVerificationEmail(normalizedEmail);
        setStep("otp");
      } catch (err) {
        const rawMessage = err instanceof Error ? err.message : "";
        const errorMessage = sanitizeAuthError(rawMessage, "reset");
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [convexSignIn, setPendingLocale],
  );

  const confirmCode = useCallback((code: string) => {
    // No backend call — just store the code and advance to new-password step
    setPendingCode(code);
    setStep("new-password");
  }, []);

  const setNewPassword = useCallback(
    async (password: string, confirm: string): Promise<void> => {
      setError(null);

      // Client-side validation: passwords must match
      if (password !== confirm) {
        setError(i18n.t("auth:errors.passwordMismatch"));
        return;
      }

      // Client-side validation: minimum 8 characters
      if (password.length < 8) {
        setError(i18n.t("auth:errors.passwordTooShort"));
        return;
      }

      if (!verificationEmail || !pendingCode) {
        setError("Missing email or verification code.");
        return;
      }

      setIsLoading(true);

      try {
        const formData = new FormData();
        formData.set("email", verificationEmail);
        formData.set("code", pendingCode);
        formData.set("newPassword", password);
        formData.set("flow", "reset-verification");

        await convexSignIn("password", formData);

        options.onSuccess?.();
      } catch (err) {
        const rawMessage = err instanceof Error ? err.message : "";
        const errorMessage = sanitizeAuthError(rawMessage, "otp");
        setError(errorMessage);
        // If OTP was wrong, move user back to OTP step so they can retry
        setStep("otp");
        setPendingCode(null);
      } finally {
        setIsLoading(false);
      }
    },
    [convexSignIn, verificationEmail, pendingCode, options],
  );

  const resendCode = useCallback(async () => {
    if (!verificationEmail) return;
    await requestReset(verificationEmail);
    // After resend, stay on OTP step (requestReset advances to "otp" already)
  }, [verificationEmail, requestReset]);

  return {
    step,
    isLoading,
    error,
    verificationEmail,
    pendingCode,
    requestReset,
    confirmCode,
    setNewPassword,
    resendCode,
    backToEmail,
    backToOtp,
    clearError,
  };
}
